import { memo, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import {
  setBookmarkColorFilter,
  showCreatedSnippetTimeDistance,
} from "rtk-slice"

import { getFormattedDistanceToNow } from "utils/getFormattedDistanceToNow"

import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"

import { format, parseISO } from "date-fns"
import { BookmarkShape, ChevronUpWide, PencilColored } from "ui-icons"

import CardAction from "./CardAction"
import Cardheader from "./CardHeader"
import CodeBlock from "./CodeBlock"

const SnippetCard = ({
  data: {
    title,
    folder,
    type,
    id,
    createdAt,
    flagged,
    starred,
    lastEdit,
    bookmarkColor,
    fontColor,
    isPinned,
    body,
    plainText,
    settings,
  },
  index,
}) => {
  const [showSnippetCard, setShowSnippetCard] = useState(false)
  const {
    editorSettings: { theme, fullscreen },
  } = settings
  const {
    palette: {
      background: { card },
      primary: { main },
    },
  } = useTheme()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const memoSearchParams = useMemo(() => searchParams, [searchParams])

  const showCreatedSnippetTimeDistanceState = useSelector(
    showCreatedSnippetTimeDistance
  )

  const handleExpandClick = () => {
    setShowSnippetCard((prev) => !prev)
  }
  const handleBookmarkColorFilter = () => {
    dispatch(setBookmarkColorFilter(bookmarkColor))
  }

  return (
    <Paper
      sx={{
        borderTopLeftRadius: index % 2 === 0 && "2em",
        borderTopRightRadius: index % 2 !== 0 && "2em",
        overflow: "hidden",
        borderTop: bookmarkColor && `2px solid ${bookmarkColor}`,
        boxShadow:
          "3px 2px 15px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",

        width: "100%",
        "& > *": {
          bgcolor: "background.card",
        },
      }}
    >
      <Box
        direction="row"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: "0.5em",
          pl: "2em",
        }}
      >
        {showCreatedSnippetTimeDistanceState ? (
          <Typography
            sx={{
              alignSelf: "center",
              fontSize: "1.1rem",
              flex: 1,
            }}
            color="info.main"
          >
            Created {getFormattedDistanceToNow(parseISO(createdAt))}
          </Typography>
        ) : null}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            justifySelf: "flex-end !important",
            p: "0.5em",
          }}
          spacing="0.5em"
        >
          <Tooltip title="Edit snippet">
            <IconButton
              aria-label="edit"
              to={`edit/${id.slice(0, 8)}?${memoSearchParams}`}
            >
              <PencilColored width="24" height="24" />
            </IconButton>
          </Tooltip>
          {bookmarkColor ? (
            <Tooltip title="Bookmark Color">
              <IconButton onClick={handleBookmarkColorFilter}>
                <BookmarkShape
                  width="24"
                  height="24"
                  fill={bookmarkColor}
                />
              </IconButton>
            </Tooltip>
          ) : null}
          <IconButton
            sx={{
              mr: 1,
              p: "0.25em",
              width: 36,
              height: 36,
              transform: showSnippetCard
                ? "rotate(0deg)"
                : "rotate(180deg)",
            }}
            onClick={handleExpandClick}
          >
            <ChevronUpWide fill={main} />
          </IconButton>
        </Box>
      </Box>

      <Card
        key={id}
        sx={{
          transformOrigin: "top right",
          maxWidth: "100%",
          bgcolor: card,
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          bgcolor="background.card"
        >
          <Cardheader
            title={title}
            folder={folder}
            snippetType={type}
          />
        </Stack>
        {showSnippetCard ? (
          <Collapse
            in={showSnippetCard}
            timeout="auto"
            unmountOnExit={false}
          >
            <CardContent
              sx={{
                bgcolor: "background.card",
                "&.MuiCardContent-root": {
                  pb: "0.5em",
                  width: "100%",
                },
              }}
            >
              <CodeBlock
                title={title}
                body={body || ""}
                type={type}
                id={id}
                theme={theme}
                fullscreen={fullscreen}
                showSnippetCard={showSnippetCard}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                py="1em"
              >
                <Typography fontSize="1rem">
                  {format(parseISO(createdAt), "eee dd MMM y")} at{" "}
                  {format(parseISO(createdAt), "HH:mm:ss")}
                </Typography>
                {lastEdit && (
                  <Typography fontSize="1rem" color="success.main">
                    Last Edit:{" "}
                    {format(parseISO(lastEdit), "dd MMM y HH:mm:ss")}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Collapse>
        ) : null}
      </Card>
      {showSnippetCard ? (
        <CardAction
          id={id}
          title={title}
          starred={starred}
          flagged={flagged}
          isPinned={isPinned}
          bookmarkColor={bookmarkColor}
          type={type}
          body={body}
          plainText={plainText}
          fontColor={fontColor}
          handleExpandClick={handleExpandClick}
        />
      ) : null}
    </Paper>
  )
}

export default memo(SnippetCard)
